import { useState } from 'react';
import { iaApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { messageIa } from '../../lib/ia';
import { C } from '../../theme/palette';

/** Tours renvoyés au serveur avec chaque question : au-delà, le début s'oublie. */
const TOURS_RENVOYES = 8;

/** Questions proposées à qui ne sait pas par où commencer. */
const SUGGESTIONS = [
  "Qui me doit de l'argent ?",
  'Quels chantiers sont en cours ?',
  'Que dois-je recommander en stock ?',
  'Quels rendez-vous cette semaine ?',
];

/**
 * L'assistant : une question sur l'affaire, une réponse courte.
 *
 * Le serveur bâtit lui-même ce que le modèle voit — facturation du mois,
 * impayés, chantiers, stock, agenda — et n'y met jamais la paie. L'écran ne
 * garde la conversation que le temps de la page : rien n'est enregistré, ni
 * ici ni sur le serveur.
 *
 * @returns {import('react').ReactElement} la carte de l'assistant
 */
export function AssistantIa() {
  const [question, setQuestion] = useState('');
  const [historique, setHistorique] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const envoie = async (texte) => {
    const q = String(texte ?? question).trim();
    if (!q || enCours) return;
    const precedents = historique.slice(-TOURS_RENVOYES);
    setHistorique((h) => [...h, { role: 'utilisateur', contenu: q }]);
    setQuestion('');
    setErreur('');
    setEnCours(true);
    try {
      const { reponse } = await iaApi.assistant({ question: q, historique: precedents });
      setHistorique((h) => [...h, { role: 'assistant', contenu: reponse }]);
    } catch (e) {
      setErreur(messageIa(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="💬"
        title="Assistant"
        color={C.purple}
        sub="Répond sur tes données · rien n'est conservé"
      />
      <div style={{ padding: 13 }}>
        {historique.length === 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => envoie(s)}
                style={{
                  background: C.purpleL,
                  color: C.purple,
                  border: '1px solid ' + C.purple + '40',
                  borderRadius: 99,
                  padding: '6px 10px',
                  fontSize: 9.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div role="log" aria-label="Conversation avec l'assistant">
          {historique.map((tour, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: tour.role === 'assistant' ? 'flex-start' : 'flex-end',
                marginBottom: 7,
              }}
            >
              <div
                style={{
                  maxWidth: '88%',
                  background: tour.role === 'assistant' ? C.purpleL : C.navyXL,
                  color: C.ink,
                  borderRadius: 11,
                  padding: '9px 11px',
                  fontSize: 10.5,
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {tour.contenu}
              </div>
            </div>
          ))}
          {enCours && (
            <div style={{ fontSize: 9.5, color: C.muted, marginBottom: 7 }}>
              L&apos;assistant réfléchit…
            </div>
          )}
        </div>
        {erreur && (
          <div
            role="alert"
            style={{
              fontSize: 9.5,
              color: C.red,
              background: C.red + '15',
              borderRadius: 7,
              padding: '7px 10px',
              marginBottom: 8,
              lineHeight: 1.5,
            }}
          >
            {erreur}
          </div>
        )}
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              envoie();
            }
          }}
          rows={2}
          maxLength={1000}
          placeholder="Pose une question sur ton entreprise…"
          aria-label="Question à l'assistant"
          style={{
            width: '100%',
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '9px 10px',
            fontSize: 12,
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
            marginBottom: 8,
          }}
        />
        <Btn
          label={enCours ? 'Envoi…' : '💬 Envoyer'}
          color={C.purple}
          full
          disabled={enCours || !question.trim()}
          onClick={() => envoie()}
        />
        <div style={{ fontSize: 8.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
          L&apos;assistant voit un résumé de tes factures, chantiers, stock et rendez-vous — jamais
          les salaires ni les données du personnel. La conversation s&apos;efface en quittant la
          page.
        </div>
      </div>
    </Card>
  );
}
